import { toast } from "sonner";
import { setArchived, setUnknownStaff } from "../no-access-store.js";
import { captureApiFailure } from "../sentry.js";
import {
  EnvelopeError,
  NetworkError,
  NonEnvelopeServerError,
  StaffArchivedError,
  UnauthenticatedError,
  UnknownStaffError,
} from "./errors.js";
import { setPaletteFailure } from "./palette-failure-store.js";
import { GENERIC_SERVER_MESSAGE, OFFLINE_MESSAGE } from "./register-copy.js";

/** Set via `useQuery`/`useMutation`'s `meta` option at the call site (the command palette sets
 * `{ paletteContext: true }`) — read back here, in the one shared handler, never inspected by
 * the call site itself. */
function isPaletteContext(meta: Record<string, unknown> | undefined): boolean {
  return meta?.["paletteContext"] === true;
}

function announce(message: string, paletteContext: boolean): void {
  if (paletteContext) {
    setPaletteFailure(message);
  } else {
    toast.error(message);
  }
}

/**
 * The ONE failure handler (TASK.md "The API client error model"), wired to both
 * QueryCache.onError and MutationCache.onError (./query-client.ts) — never per-retry, never
 * per-component. Every error is exactly one of the five typed failures (./errors.ts); this
 * function is the single place deciding toast vs. palette-inline vs. redirect vs. full-stop,
 * and the single place a Sentry event fires for it.
 *
 * `navigateToSignIn` is injected rather than importing the router singleton directly — this
 * module has no import-time dependency on the router, and is trivial to unit test with a spy.
 */
export function handleApiFailure(
  error: unknown,
  meta: Record<string, unknown> | undefined,
  navigateToSignIn: () => void,
): void {
  const paletteContext = isPaletteContext(meta);

  if (error instanceof UnauthenticatedError) {
    // 401 — normal life, not an error (TASK.md). No toast, no Sentry; the redirect IS the
    // honest state, regardless of where the call originated.
    navigateToSignIn();
    return;
  }

  if (error instanceof UnknownStaffError) {
    // 401 WITH a token: Clerk's session is live, the API refuses it. Redirecting to /sign-in
    // would bounce back instantly and forever (the production loop of 2026-08-02) — this is a
    // full stop, not a sign-out. No toast (the screen IS the message), no Sentry (a
    // provisioning state, not a fault).
    setUnknownStaff(true);
    return;
  }

  if (error instanceof StaffArchivedError) {
    // 403 auth.staff_archived — the full stop. No toast, no Sentry, no retry loop.
    setArchived(true);
    return;
  }

  if (error instanceof EnvelopeError) {
    announce(error.message, paletteContext);
    captureApiFailure(error, { code: error.code, requestId: error.requestId });
    return;
  }

  if (error instanceof NetworkError) {
    announce(OFFLINE_MESSAGE, paletteContext);
    // No requestId exists — the request never reached a server to be assigned one.
    captureApiFailure(error, { requestId: "none (network failure)" });
    return;
  }

  if (error instanceof NonEnvelopeServerError) {
    announce(GENERIC_SERVER_MESSAGE, paletteContext);
    captureApiFailure(error, { requestId: error.requestId ?? "none (no X-Request-ID header)" });
    return;
  }

  // Outside the contracted taxonomy — a bug, not a named case. Still surfaced, never swallowed
  // (.claude/rules/core.md).
  announce(GENERIC_SERVER_MESSAGE, paletteContext);
  captureApiFailure(error, { requestId: "none (unrecognized error shape)" });
}
